/* trademarks: the portfolio list (`tm`), one trademark's dossier (`tmd`) and
 * the add form (`tmAdd`) — translated in the Wave 3 pass (2026-09-30).
 *
 * `tm.status`, `tm.type` and `tm.monitoringTag` are shared by all three pages
 * and the portfolio row; the service reads them for its labels. */
export default {
  tm: {
    title: 'My Trademarks',
    subtitle: 'Manage monitored trademarks, jurisdictions, legal status, and monitoring activity.',
    filters: 'Filters',
    add: 'Add trademark',
    kpi: {
      label: 'Portfolio overview',
      total: 'Total trademarks',
      thisMonth: 'this month',
      noneThisMonth: 'No additions this month',
      monitored: 'Active monitoring',
      ofPortfolio: 'of portfolio monitored',
      openMatches: 'Matches awaiting review',
      openMatchesHint: 'Across the whole portfolio',
      openMatchesNone: 'Nothing waiting',
      registries: 'Registries covered',
      registriesNone: 'No monitoring configured',
    },
    filter: {
      search: 'Search',
      searchPlaceholder: 'Trademark or owner name',
      monitoring: 'Monitoring status',
      monitored: 'Monitored',
      notMonitored: 'Not monitored',
      registry: 'Registry',
      noRegistries: 'No registries configured yet.',
      classes: 'Nice classes',
      classesHint: 'Classes present on this page, commonest first.',
      type: 'Trademark type',
      reset: 'Reset',
      apply: 'Apply',
    },
    chip: {
      registry: 'Registry: {code}',
      class: 'Class {n}',
      search: 'Search: {q}',
    },
    sort: {
      label: 'Sort trademarks',
      title: 'Name (A–Z)',
      titleDesc: 'Name (Z–A)',
      recent: 'Recently added',
      registered: 'Registration date, newest first',
      expiring: 'Expiry date, earliest first',
    },
    bulk: {
      confirmTitle: 'Delete {n} trademark? | Delete {n} trademarks?',
      cannotUndo: 'This cannot be undone.',
      goesWith: 'Their monitoring scopes, documents and match history go with them.',
      partial: 'Deleted {done} of {n}',
      partialSub: '{n} could not be removed.',
      done: 'Deleted {n} trademark | Deleted {n} trademarks',
    },
    shown: '{n} of {total} trademark | {n} of {total} trademarks',
    filtersActive: '{n} filter active | {n} filters active',
    searchPlaceholder: 'Search trademarks…',
    col: {
      trademark: 'Trademark',
      owner: 'Owner',
      juris: 'Juris.',
      classes: 'Classes',
      monitoring: 'Monitoring',
      status: 'Status',
      review: 'Review',
    },
    empty: {
      title: 'No trademarks match your filters',
      message: 'Try clearing the registry or class selection, or searching for a different name.',
      reset: 'Reset filters',
    },
    page: {
      showing: 'Showing',
      ofTotal: 'of {total} trademark | of {total} trademarks',
      prev: 'Previous page',
      next: 'Next page',
    },
    preview: {
      label: 'Selected trademark preview',
      noNumber: 'No number',
      reg: 'Reg. {date}',
      close: 'Close preview',
      owner: 'Owner',
      country: 'Country',
      watchedOn: 'Watched on',
      pausedOn: 'Paused: {list}',
      classes: 'Nice classes',
      type: 'Type',
      status: 'Status',
      monitoring: 'Monitoring',
      awaiting: 'match awaiting review | matches awaiting review',
      openCases: '{n} open case | {n} open cases',
      dossier: 'View full dossier',
      openMonitoring: 'Open monitoring',
    },
    monitoring: {
      active: 'Active monitoring',
      paused: 'Monitoring paused',
      none: 'Not monitored',
    },
    monitoringTag: {
      active: 'Active',
      paused: 'Paused',
      none: 'Not monitored',
    },
    row: {
      select: 'Select {name}',
      logoAlt: '{name} logo',
      toReview: '{n} to review',
      clear: 'Clear',
      open: 'Open trademark dossier',
    },
    /* What `brands.registry_status` holds, plus the fixtures' four. */
    status: {
      registered: 'Registered',
      pending: 'Pending',
      opposition: 'Opposition',
      under_review: 'Under review',
      filed: 'Filed',
      published: 'Published',
      opposed: 'Opposed',
      cancellation_procedure: 'Cancellation',
      expired: 'Expired',
      refused: 'Refused',
      withdrawn: 'Withdrawn',
    },
    type: {
      wordmark: 'Wordmark',
      figurative: 'Figurative',
      combined: 'Combined',
      sound: 'Sound mark',
      other: 'Other',
    },
  },

  tmd: {
    back: 'Back to portfolio',
    logoAlt: '{name} logo',
    eyebrow: 'Protected trademark',
    toReviewTitle: 'Matches the AI surfaced that nobody has judged yet',
    regShort: 'Reg.',
    monitoring: 'AI Monitoring',
    monitoringActive: 'Active · {list}',
    uploading: 'Uploading…',
    upload: 'Upload document',
    openMonitoring: 'Open monitoring',
    createOpposition: 'Create opposition',
    label: 'Trademark dossier',
    sections: 'Dossier sections',
    tab: {
      overview: 'Overview',
      monitoring: 'Monitoring',
      matches: 'Matches',
      oppositions: 'Oppositions',
      documents: 'Documents',
    },
    details: {
      title: 'Registration details',
      type: 'Mark type',
      country: 'Country of registration',
      number: 'Registration or application №',
      status: 'Status',
      filed: 'Filed',
      registered: 'Registered',
      expires: 'Expires',
      owner: 'Owner',
      classes: 'Nice classes',
      description: 'Description',
    },
    scopes: {
      title: 'Monitoring scopes',
      count: '{n} registry | {n} registries',
      emptyTitle: 'This mark is not being watched',
      empty:
        'No registry has been chosen for this mark, so new publications are not checked against it.',
      registry: 'Registry',
      state: 'State',
      lastScan: 'Last scanned',
      active: 'Active',
      paused: 'Paused',
      awaiting: 'Awaiting first scan (3-month backfill)',
      never: 'Not scanned',
      feedFallback: 'Feed {id}',
    },
    matches: {
      title: 'Related matches',
      awaiting: '{n} awaiting review',
      found: 'Found',
      registry: 'Registry',
      decidedBy: 'Decided by',
      risk: 'Risk',
      window: 'Window',
      rule: 'Rule match',
      review: 'Review',
      emptyTitle: 'No matches found for this mark',
      empty:
        'Nothing published in the registries you watch has been found similar to this trademark yet.',
      more: 'The {n} with the soonest opposition window are shown. The rest are on the monitoring page, under this trademark.',
      closesToday: 'Closes today',
      closedDays: 'Closed {n} day ago | Closed {n} days ago',
      closedMonths: 'Closed {n} month ago | Closed {n} months ago',
      closedYears: 'Closed {n} year ago | Closed {n} years ago',
    },
    docs: {
      title: 'Documents',
      count: '{n} file | {n} files',
      emptyTitle: 'No documents yet',
      empty: 'Registration certificates, evidence of use, and correspondence live here.',
      uploadFirst: 'Upload a file',
      file: 'File',
      kind: 'Kind',
      size: 'Size',
      added: 'Added',
      remove: 'Delete {name}',
      rejected: 'rejected',
      notUploaded: '{n} file not uploaded | {n} files not uploaded',
      uploaded: 'Uploaded {n} file | Uploaded {n} files',
      confirmTitle: 'Delete {name}?',
      confirmBody: 'The file is removed permanently. This cannot be undone.',
      deleted: 'File deleted',
      deleteFailed: 'Could not delete the file',
    },
    rail: {
      label: 'Protection summary',
      title: 'Protection summary',
      watchedOn: 'Watched on',
      none: 'None',
      classes: 'Nice classes',
      noneRecorded: 'None recorded',
      toReview: 'Matches to review',
      openCases: 'Open cases',
    },
  },

  tmAdd: {
    crumb: 'Add trademark',
    title: 'Add Trademark',
    subtitle: 'Register a mark you protect and choose which registries to watch for conflicts.',
    creating: 'Creating…',
    create: 'Create trademark',
    loadFailed: 'Could not load: {list}.',
    failedPart: {
      clients: 'clients',
      registries: 'registries',
      classes: 'Nice classes',
    },
    workspace: 'Add trademark workspace',
    required: 'Required',
    example: 'e.g. {value}',
    err: {
      title: 'Trademark name is required',
      titleShort: 'At least {n} characters',
      client: 'Pick the client who owns this mark',
      country: 'Country of registration is required',
      type: 'Pick a trademark type',
      classes: 'Pick at least one Nice class',
      incomplete: 'Please complete the required fields',
    },
    mark: {
      title: 'The mark',
      sub: 'What is protected, and who owns it.',
      name: 'Trademark name',
      namePlaceholder: 'The mark as filed',
      country: 'Country of registration',
      countryPick: 'Select country…',
      countryHint: 'Decides which national registry may be watched.',
      owner: 'Owner (client)',
      ownerPick: 'Select client…',
      clientCount: '{n} client in your workspace | {n} clients in your workspace',
      addClient: 'add a new one',
      type: 'Trademark type',
      description: 'Description',
      descriptionPlaceholder:
        'Goods and services in your own words, or anything the next person should know.',
    },
    typeHint: {
      word: 'Text only — the name itself is protected.',
      figurative: 'A logo or device with no wording.',
      combined: 'Wording and device together.',
      sound: 'A jingle or audio signature.',
      other: 'Shape, colour, motion, position.',
    },
    registration: {
      title: 'Registration',
      sub: 'Optional — a mark can be monitored before it is granted.',
      application: 'Application №',
      registration: 'Registration №',
      filed: 'Filed on',
      registered: 'Registered on',
      expires: 'Expires on',
    },
    scopes: {
      title: 'Where to watch',
      sub: 'Each registry selected is scanned for conflicts with this mark.',
      selected: '{n} selected',
      hint: 'A mark with no registry chosen is stored but never scanned.',
      inactive: 'Not currently active',
      countryFirst: 'Choose the country first',
      onlyFor: 'Only available for {code} marks',
      noSource: 'Your firm has no source configured for this registry',
    },
    feedKind: {
      world: 'Worldwide (Madrid System)',
      europe: 'European Union',
      national: 'National office',
    },
    classes: {
      title: 'Nice classes',
      sub: 'The goods and services this mark covers. Conflicts are judged within them.',
      search: 'Search all 45 classes by number or wording…',
      searchLabel: 'Search Nice classes',
    },
    next: {
      label: 'Guidance',
      title: 'What happens next',
      now: 'Now.',
      nowText: 'The mark is filed under its client and appears in the portfolio.',
      scan: 'First scan.',
      scanText:
        'Each new scope starts with a three-month backfill — the opposition window — so nothing already published is missed.',
      after: 'Afterwards.',
      afterText: "Add the registration certificate and any evidence on the mark's dossier.",
    },
    actions: 'Form actions',
    unsaved: 'Nothing is saved until you create the trademark.',
    created: 'Trademark created',
    createdWatched:
      '{name} is now watched on {n} registry. | {name} is now watched on {n} registries.',
    createdUnwatched: '{name} added. No monitoring scope selected yet.',
    createFailed: 'Could not create the trademark',
  },
} as const
